import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";

import clsx from "clsx";

import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import MessageErrorBoundary from "../MessageErrorBoundary";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    border: "none",
    borderRadius: 16,
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },

  mainWrapperShift: {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
    [theme.breakpoints.down('md')]: {
      marginRight: 0,
    },
  },
  tagsCard: {
    marginLeft: 8,
    marginRight: 8,
    marginTop: 2,
    marginBottom: 2,
    width: "calc(100% - 16px)",
    maxWidth: "calc(100% - 16px)",
    alignSelf: "center",
    paddingTop: 1,
    paddingBottom: 1,
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    boxShadow: "none",
  },
  headerSearchButton: {
    marginLeft: 4
  },
  ticketSearchPanel: {
    position: "absolute",
    top: 76,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 20,
    width: 560,
    maxWidth: "calc(100% - 32px)",
    borderRadius: 10,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.18)"
  },
  ticketSearchContent: {
    padding: theme.spacing(1.25, 1.5),
    "&:last-child": {
      paddingBottom: theme.spacing(1.25)
    }
  },
  ticketSearchBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  ticketSearchCounter: {
    minWidth: 76,
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  draftHeaderInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    paddingRight: theme.spacing(2)
  },
  draftTitle: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: 1.2
  },
  draftSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  draftMessagesArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: "center",
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    backgroundRepeat: "repeat"
  },
  draftInputBar: {
    flex: "none",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  draftInput: {
    flex: 1,
    minHeight: 44,
    borderRadius: 22,
    padding: theme.spacing(0.75, 2),
    background: theme.mode === 'light' ? "#f0f2f5" : "#2a3942",
    color: theme.palette.text.primary
  }
}));

const TicketSearchDialog = React.memo(({
  open,
  onClose,
  onSearch,
  onNavigate,
  loading,
  loadedCount,
  totalCount,
  currentIndex,
  classes
}) => {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const focusTimer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(focusTimer);
  }, [open]);

  const handleSubmit = () => {
    const term = value.trim();
    if (term.length < 2) return;
    onSearch(term);
    inputRef.current?.focus();
  };

  const resultText = totalCount > 0 ? `${currentIndex + 1}/${totalCount}` : "0/0";

  if (!open) return null;

  return (
    <Paper className={classes.ticketSearchPanel} elevation={6}>
      <Box className={classes.ticketSearchContent}>
        <Box className={classes.ticketSearchBar}>
          <Tooltip title="Buscar">
            <span>
              <IconButton
                color="primary"
                size="small"
                onClick={handleSubmit}
                disabled={loading || value.trim().length < 2}
              >
                <SearchIcon />
              </IconButton>
            </span>
          </Tooltip>
          <TextField
            inputRef={inputRef}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar neste ticket"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onClose();
              }
            }}
          />
          <Typography className={classes.ticketSearchCounter}>
            {loading ? <CircularProgress size={16} /> : resultText}
          </Typography>
          <Tooltip title="Ocorrência anterior">
            <span>
              <IconButton
                size="small"
                onClick={() => onNavigate("prev")}
                disabled={loading || loadedCount === 0}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Próxima ocorrência">
            <span>
              <IconButton
                size="small"
                onClick={() => onNavigate("next")}
                disabled={loading || loadedCount === 0}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fechar">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
});

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);
  const [ticketSearchOpen, setTicketSearchOpen] = useState(false);
  const [ticketSearchLoading, setTicketSearchLoading] = useState(false);
  const [ticketSearchResults, setTicketSearchResults] = useState([]);
  const [ticketSearchCount, setTicketSearchCount] = useState(0);
  const [ticketSearchIndex, setTicketSearchIndex] = useState(0);
  const { companyId } = user;
  const isDraftTicket = Boolean(ticket?.isDraft);

  const handleDraftSent = useCallback((nextTicket) => {
    if (!nextTicket) return;

    setTabOpen(nextTicket.status === "group" ? "group" : "open");

    if (nextTicket.uuid) {
      history.replace(`/tickets/${nextTicket.uuid}`);
    } else if (nextTicket.id) {
      history.replace(`/tickets/${nextTicket.id}`);
    }
  }, [history, setTabOpen]);

  const syncAutoAssumedTicketNavigation = useCallback((nextTicket, previousStatus) => {
    const destinationFilter = nextTicket?.status === "group" ? "group" : "open";
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", destinationFilter);

    console.info("[TICKET_AUTO_ASSUMED]", {
      ticketId: nextTicket?.id,
      oldStatus: previousStatus,
      newStatus: nextTicket?.status,
      userId: user?.id
    });

    console.info("[TICKET_AUTO_NAVIGATION]", {
      ticketId: nextTicket?.id,
      originFilter: previousStatus,
      destinationFilter
    });

    requestAnimationFrame(() => {
      setTabOpen(destinationFilter);
    });

    history.replace({
      pathname: `/tickets/${nextTicket?.uuid || ticketId}`,
      search: `?${searchParams.toString()}`
    });
  }, [history, location.search, setTabOpen, ticketId, user?.id]);

  useEffect(() => {
    setLoading(true);
    const fetchTicket = async () => {
      try {

        if (!isNil(ticketId) && ticketId !== "undefined") {

          const { data } = await api.get("/tickets/u/" + ticketId);

          setContact(data.contact);
          // setWhatsapp(data.whatsapp);
          // setQueueId(data.queueId);
          setTicket(data);
          
          // Verificar se há parâmetro 'tab' na URL - TEM PRIORIDADE ABSOLUTA
          const urlParams = new URLSearchParams(window.location.search);
          const tabParam = urlParams.get('tab');
          
          // Se houver parâmetro tab na URL, usar ele SEMPRE (tem prioridade absoluta)
          // NÃO usar o status do ticket se houver parâmetro na URL
          if (tabParam && ["pending", "open", "group", "closed"].includes(tabParam)) {
            // Usar requestAnimationFrame para garantir que seja aplicado depois que o TicketsManagerTabs carregar
            requestAnimationFrame(() => {
              setTabOpen(tabParam);
            });
          } else {
            // Só usar o status do ticket se NÃO houver parâmetro na URL
            // E apenas se o status for válido
            if (["pending", "open", "group"].includes(data.status)) {
              requestAnimationFrame(() => {
                setTabOpen(data.status);
              });
            }
          }
          setLoading(false);
        }
      } catch (err) {
        history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
        setLoading(false);
        toastError(err);
      }
    };
    fetchTicket();
  }, [ticketId, user, history]);

  useEffect(() => {
    if (isDraftTicket) {
      return;
    }

    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId) {
      //    const socket = socketManager.GetSocket();

      const onConnectTicket = () => {
        socket.emit("joinChatBox", `${ticket.id}`);
      }

      const onCompanyTicket = (data) => {
        // ✅ GROUP_DEBUG: Log quando recebe evento de ticket
        if (import.meta.env.VITE_ENABLE_GROUP_DEBUG === "true") {
          console.log("[GROUP_DEBUG] ticket event received (Ticket component)", {
            action: data.action,
            ticketId: data.ticket?.id,
            currentTicketId: ticket?.id,
            ticketStatus: data.ticket?.status,
            ticketIsGroup: data.ticket?.isGroup || false,
            contactIsGroup: data.ticket?.contact?.isGroup || false,
            contactId: data.ticket?.contactId,
            remoteJid: data.ticket?.contact?.remoteJid
          });
        }

        if (data.action === "update" && data.ticket.id === ticket?.id) {
          console.log("[SOCKET_TICKET_UPDATE]", {
            source: "Ticket",
            ticketId: data.ticket?.id || null,
            ticketUuid: data.ticket?.uuid || null,
            currentTicketId: ticket?.id || null,
            lastMessage: data.ticket?.lastMessage || null
          });
          const previousStatus = ticket?.status;
          const nextStatus = data.ticket?.status;
          const wasAutoAssumedByCurrentUser =
            previousStatus === "pending" &&
            ["open", "group"].includes(nextStatus) &&
            data.ticket?.userId === user?.id;

          if (wasAutoAssumedByCurrentUser) {
            syncAutoAssumedTicketNavigation(data.ticket, previousStatus);
          }

          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          // if (isMounted) {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
          // }
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {

        socket.emit("joinChatBoxLeave", `${ticket.id}`);
        socket.off("connect", onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      };
    }
  }, [ticketId, ticket, history, companyId, socket, syncAutoAssumedTicketNavigation, user?.companyId, user?.id, isDraftTicket]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleQuickMessageSelect = (quickMessage) => {
    try {
      if (quickMessage.message) {
        // Disparar evento que o MessageInput vai escutar
        const event = new CustomEvent('insertQuickMessage', {
          detail: { message: quickMessage.message }
        });
        window.dispatchEvent(event);
        
      }
      
      if (quickMessage.mediaPath) {
        // Tratar mídia se necessário
      }
    } catch (error) {
      console.error("Erro ao inserir resposta rápida:", error);
      toastError("Erro ao inserir resposta rápida");
    }
  };

  const handleOpenTicketSearch = useCallback(() => {
    setTicketSearchOpen(true);
    setTicketSearchResults([]);
    setTicketSearchCount(0);
    setTicketSearchIndex(0);
  }, []);

  const handleCloseTicketSearch = useCallback(() => {
    setTicketSearchOpen(false);
  }, []);

  const handleSearchInTicket = async (searchTerm) => {
    const term = searchTerm.trim();
    if (term.length < 2 || !ticket?.uuid) {
      setTicketSearchResults([]);
      setTicketSearchCount(0);
      setTicketSearchIndex(0);
      return;
    }

    try {
      setTicketSearchLoading(true);
      const { data } = await api.get(`/messages/${ticket.uuid}/search`, {
        params: { searchParam: term }
      });
      const results = data.messages || [];
      setTicketSearchResults(results);
      setTicketSearchCount(data.count || 0);
      setTicketSearchIndex(0);
      if (results[0]?.id) {
        history.push(`/tickets/${ticket.uuid}?focusedMessageId=${results[0].id}`);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setTicketSearchLoading(false);
    }
  };

  const handleNavigateSearchResult = useCallback((direction) => {
    if (!ticketSearchResults.length || !ticket?.uuid) return;
    setTicketSearchIndex((current) => {
      const nextIndex = direction === "prev"
        ? (current - 1 + ticketSearchResults.length) % ticketSearchResults.length
        : (current + 1) % ticketSearchResults.length;
      const messageId = ticketSearchResults[nextIndex]?.id;
      if (messageId) {
        history.push(`/tickets/${ticket.uuid}?focusedMessageId=${messageId}`);
      }
      return nextIndex;
    });
  }, [history, ticket?.uuid, ticketSearchResults]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        handleOpenTicketSearch();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleOpenTicketSearch]);

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          ticketDbId={ticket.id}
          ticketUuid={ticket.uuid}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
          ticketStatus={ticket.status}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          contactId={contact.id}
          whatsappId={ticket.whatsappId}
          ticket={ticket}
          contact={contact}
        />
      </>
    );
  };

  const renderDraftTicket = () => (
    <>
      <TicketHeader loading={loading}>
        {ticket.contact !== undefined && (
          <div id="TicketHeader">
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={() => {}}
            />
          </div>
        )}
      </TicketHeader>

      <Paper className={classes.tagsCard} elevation={0}>
        <TagsContainer contact={contact} />
      </Paper>

      <Box className={classes.draftMessagesArea}>
        <Typography variant="body2">
          Envie uma mensagem para iniciar ou reutilizar um ticket aberto deste contato.
        </Typography>
      </Box>

      <ReplyMessageProvider>
        <ForwardMessageProvider>
          <EditMessageProvider>
            <MessageInput
              ticketId={null}
              ticketStatus="open"
              ticketChannel="whatsapp"
              droppedFiles={[]}
              contactId={null}
              whatsappId={ticket.whatsappId}
              ticket={ticket}
              contact={contact}
              draftMode
              draftUuid={ticket.draftUuid || ticket.uuid}
              onDraftSent={handleDraftSent}
            />
          </EditMessageProvider>
        </ForwardMessageProvider>
      </ReplyMessageProvider>
    </>
  );


  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen && !isMobile,
        }, "neumorphic-card")}
        elevation={0}
      >
        {isDraftTicket ? renderDraftTicket() : (
          <>
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
        <TicketActionButtons
          ticket={ticket}
          contact={contact}
          onQuickMessageSelect={handleQuickMessageSelect}
        />
        <Tooltip title="Buscar neste ticket (Ctrl+F)">
          <IconButton
            className={classes.headerSearchButton}
            color="primary"
            onClick={handleOpenTicketSearch}
            aria-label="Buscar neste ticket"
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
        </TicketHeader>
        {/* </div> */}
        <TicketSearchDialog
          open={ticketSearchOpen}
          onClose={handleCloseTicketSearch}
          onSearch={handleSearchInTicket}
          onNavigate={handleNavigateSearchResult}
          loading={ticketSearchLoading}
          loadedCount={ticketSearchResults.length}
          totalCount={ticketSearchCount}
          currentIndex={ticketSearchIndex}
          classes={classes}
        />

        <Paper className={classes.tagsCard} elevation={0}>
          <TagsContainer contact={contact} />
        </Paper>
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              <MessageErrorBoundary context={{ ticketId: ticket?.id, companyId: user?.companyId }}>
                {renderMessagesList()}
              </MessageErrorBoundary>
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
          </>
        )}
      </Paper>

      {!isDraftTicket && (
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={handleDrawerClose}
          contact={contact}
          loading={loading}
          ticket={ticket}
        />
      )}

    </div>
  );
};

export default Ticket;
